import React, { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import SplashScreen from "./components/guidance/SplashScreen";
import { ErrorBoundary } from "./components/ErrorBoundary";

// Pages
import HomePage from "./pages/HomePage";
import LiveNowPage from "./pages/LiveNowPage";
import MarketplacePage from "./pages/MarketplacePage";
import ServicesPage from "./pages/ServicesPage";
import WalletPage from "./pages/WalletPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import VendorDashboard from "./pages/VendorDashboard";
import ProductDetailPage from "./pages/ProductDetailPage";
import FlashDealPage from "./pages/FlashDealPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import OrdersPage from "./pages/OrdersPage";
import BookingPage from "./pages/BookingPage";
import SettingsPage from "./pages/SettingsPage";
import SalesDashboardPage from "./pages/SalesDashboardPage";
import DealRoomsListPage from "./pages/DealRoomsListPage";
import DealRoomPage from "./pages/DealRoomPage";
import DealHistoryPage from "./pages/DealHistoryPage";
import CreatorHubPage from "./pages/CreatorHubPage";
import CreatorRoomPage from "./pages/CreatorRoomPage";
import CreatorProfilePage from "./pages/CreatorProfilePage";
import CreatorDiscoveryPage from "./pages/CreatorDiscoveryPage";
import CreatorDashboard from "./pages/CreatorDashboard";
import ModerationPage from "./pages/ModerationPage";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import CreateProjectPage from "./pages/CreateProjectPage";
import EventsPage from "./pages/EventsPage";
import EventDetailPage from "./pages/EventDetailPage";
import CreateEventPage from "./pages/CreateEventPage";
import InvestorDashboard from "./pages/InvestorDashboard";
import ActivityFeedPage from "./pages/ActivityFeedPage";
import ArtistProfilePage from "./pages/ArtistProfilePage";
import StudioPage from "./pages/StudioPage";
import NegotiationsPage from "./pages/NegotiationsPage";
import RevenuePage from "./pages/RevenuePage";
import LocalScenePage from "./pages/LocalScenePage";
import AlertCenterPage from "./pages/AlertCenterPage";
import CityHeatmapPage from "./pages/CityHeatmapPage";
import MarketingHubPage from "./pages/MarketingHubPage";
import SmartLinkPage from "./pages/SmartLinkPage";
import LandingPageView from "./pages/LandingPageView";
import SuperAdminPage from "./pages/SuperAdminPage";
import MarketingSuitePage from "./pages/MarketingSuitePage";
import PublicLandingPage from "./pages/PublicLandingPage";
import ClientReportPage from "./pages/ClientReportPage";

// Components
import MainLayout from "./components/layout/MainLayout";
import AIChatbot from "./components/shared/AIChatbot";
import { SmartNotificationProvider } from "./components/guidance/SmartNotificationProvider";

function ProtectedRoute({ children, vendorOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-lime border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (vendorOnly && user.role !== 'vendor') return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <>
      <SmartNotificationProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />

          <Route element={<MainLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/live" element={<LiveNowPage />} />
            <Route path="/marketplace" element={<MarketplacePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/product/:id" element={<ProductDetailPage />} />
            <Route path="/flash-deal/:id" element={<FlashDealPage />} />
            <Route path="/services/:id" element={<BookingPage />} />

            <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
            <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/rooms" element={<ProtectedRoute><DealRoomsListPage /></ProtectedRoute>} />
            <Route path="/admin/sales" element={<ProtectedRoute><SalesDashboardPage /></ProtectedRoute>} />
            <Route path="/admin/deals" element={<ProtectedRoute><DealHistoryPage /></ProtectedRoute>} />
            <Route path="/creators" element={<ProtectedRoute><CreatorDiscoveryPage /></ProtectedRoute>} />
            <Route path="/creators/old" element={<ProtectedRoute><CreatorHubPage /></ProtectedRoute>} />
            <Route path="/creator/profile/:userId" element={<ProtectedRoute><CreatorProfilePage /></ProtectedRoute>} />
            <Route path="/creator/dashboard" element={<ProtectedRoute><CreatorDashboard /></ProtectedRoute>} />

            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/projects/create" element={<ProtectedRoute><CreateProjectPage /></ProtectedRoute>} />
            <Route path="/projects/:id" element={<ProjectDetailPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/events/create" element={<ProtectedRoute><CreateEventPage /></ProtectedRoute>} />
            <Route path="/events/:id" element={<EventDetailPage />} />
            <Route path="/investor" element={<ProtectedRoute><InvestorDashboard /></ProtectedRoute>} />
            <Route path="/feed" element={<ActivityFeedPage />} />
            <Route path="/artist/:id" element={<ArtistProfilePage />} />
            <Route path="/studio" element={<ProtectedRoute><StudioPage /></ProtectedRoute>} />
            <Route path="/negotiations" element={<ProtectedRoute><NegotiationsPage /></ProtectedRoute>} />
            <Route path="/revenue" element={<ProtectedRoute><RevenuePage /></ProtectedRoute>} />
            <Route path="/local-scene" element={<LocalScenePage />} />
            <Route path="/city-heatmap" element={<CityHeatmapPage />} />
            <Route path="/marketing" element={<ProtectedRoute><MarketingHubPage /></ProtectedRoute>} />
            <Route path="/m/:slug" element={<SmartLinkPage />} />
            <Route path="/lp/:slug" element={<LandingPageView />} />
            <Route path="/notifications" element={<ProtectedRoute><AlertCenterPage /></ProtectedRoute>} />
            <Route path="/superadmin" element={<ProtectedRoute><SuperAdminPage /></ProtectedRoute>} />
            <Route path="/suite" element={<ProtectedRoute><MarketingSuitePage /></ProtectedRoute>} />

            <Route path="/vendor" element={<ProtectedRoute vendorOnly><VendorDashboard /></ProtectedRoute>} />
          </Route>

          <Route path="/room/:id" element={<ProtectedRoute><DealRoomPage /></ProtectedRoute>} />
          <Route path="/creator/:id" element={<ProtectedRoute><CreatorRoomPage /></ProtectedRoute>} />
          <Route path="/moderation" element={<ProtectedRoute><ModerationPage /></ProtectedRoute>} />
          <Route path="/page/:slug" element={<PublicLandingPage />} />
          <Route path="/report/:token" element={<ClientReportPage />} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/cancel" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </SmartNotificationProvider>
      <AIChatbot />
    </>
  );
}

function App() {
  const [splashDone, setSplashDone] = useState(false);

  return (
    <div className="App dark">
      <BrowserRouter>
        <AuthProvider>
          {!splashDone && <SplashScreen onComplete={() => setSplashDone(true)} />}
          {splashDone && <ErrorBoundary><AppRoutes /></ErrorBoundary>}
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
